import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, InputNumber, Modal, Select, Switch, message } from "antd";
import { useTranslation } from "react-i18next";

import {
  ProductChargeType,
  ProductStatus,
  type Product,
  type ProductPrice,
} from "@fitness-app/data-models/entities/Product";
import { getErrorMessage } from "@fitness-app/utils";
import { getHumanPrice } from "@fitness-app/utils/src/products/getHumanPrice";

import {
  buildCustomPricePayload,
  customPriceDefaults,
  CUSTOM_PRICE_OPTION,
  getProductCurrency,
  isCustomPriceSelected,
  ManualOrderCustomPriceFields,
  resolveSkipImpactOnClientAccess,
  type ManualOrderCustomPriceFormModel,
} from "~/modules/Products/components/ManualOrderCustomPrice/ManualOrderCustomPrice";
import { productApi, useAppSelector } from "~/store/initializeStore";

interface ManualOrderFormModel extends ManualOrderCustomPriceFormModel {
  productId: string;
  priceId: string;
  customLabel: string;
  amount: number;
  sendClientNotification: boolean;
}

const getPriceAmount = (price: ProductPrice) => (price.unit_amount ?? 0) / 100;

const getActivePaidPrices = (product: Pick<Product, "prices">): ProductPrice[] =>
  (product.prices || []).filter(
    (price): price is ProductPrice => price.active && typeof price.unit_amount === "number",
  );

interface Props {
  clientEmail: string | null;
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

export const ManualOrderModal = ({ clientEmail, open, onClose, onCreated }: Props): React.ReactElement => {
  const { t } = useTranslation("client");
  const [form] = Form.useForm<ManualOrderFormModel>();
  const [issuing, setIssuing] = useState(false);
  const { productsListData } = useAppSelector((store) => store.products);

  const productOptions = useMemo(
    () =>
      productsListData
        .filter((product) => product.chargeType === ProductChargeType.PAID && product.status === ProductStatus.ACTIVE)
        .filter((product) => getActivePaidPrices(product).length > 0)
        .map((product) => ({ label: product.name, value: product.id })),
    [productsListData],
  );

  const selectedProductId = Form.useWatch("productId", form);
  const selectedProduct = useMemo(
    () => productsListData.find((item) => item.id === selectedProductId) ?? null,
    [productsListData, selectedProductId],
  );
  const prices = useMemo(() => (selectedProduct ? getActivePaidPrices(selectedProduct) : []), [selectedProduct]);

  const selectedPriceId = Form.useWatch("priceId", form);
  const selectedPrice = useMemo(
    () => prices.find((price) => price.id === selectedPriceId) ?? null,
    [prices, selectedPriceId],
  );
  const customPriceSelected = isCustomPriceSelected(selectedPriceId);
  const customPriceType = Form.useWatch("customPriceType", form);
  const customPriceInterval = Form.useWatch("customPriceInterval", form);
  const skipImpactOnClientAccess = Form.useWatch("skipImpactOnClientAccess", form);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ sendClientNotification: true, ...customPriceDefaults });
    }
  }, [open, form]);

  const handleProductChange = (productId: string) => {
    const product = productsListData.find((item) => item.id === productId);
    const price = product ? getActivePaidPrices(product)[0] : undefined;

    form.setFieldsValue({
      priceId: price?.id as string,
      customLabel: price?.customLabel || "",
      amount: price ? getPriceAmount(price) : 0,
    });
  };

  const handlePriceChange = (priceId: string) => {
    const price = prices.find((item) => item.id === priceId);

    if (price) {
      form.setFieldsValue({ customLabel: price.customLabel || "", amount: getPriceAmount(price) });
    }
  };

  const issueOrder = async (formModel: ManualOrderFormModel) => {
    const amount = Math.round(Number(formModel.amount) * 100);
    const withCustomPrice = isCustomPriceSelected(formModel.priceId);

    if (!clientEmail || !formModel.priceId || !formModel.productId) {
      void message.warning("Uzupełnij produkt i cenę");
      return;
    }

    if (!Number.isFinite(amount) || amount < 0 || (withCustomPrice && amount === 0)) {
      void message.warning("Podaj poprawną kwotę");
      return;
    }

    try {
      setIssuing(true);
      await productApi.post("/manual-payment/create-order", {
        productId: formModel.productId,
        priceId: withCustomPrice ? null : formModel.priceId,
        customPrice: withCustomPrice ? buildCustomPricePayload(formModel, getProductCurrency(selectedProduct)) : null,
        clientEmail,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        manualOrderOverrides: {
          amount,
          priceCustomLabel: formModel.customLabel || "",
          sendClientNotification: formModel.sendClientNotification !== false,
          skipImpactOnClientAccess: resolveSkipImpactOnClientAccess(formModel, withCustomPrice),
        },
      });
      void message.success("Zamówienie wystawione");
      onClose();
      onCreated?.();
    } catch (error) {
      void message.error(`Nie udało się wystawić zamówienia: ${getErrorMessage(error)}`);
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Modal
      title={`Wystaw ręczne zamówienie${clientEmail ? ` – ${clientEmail}` : ""}`}
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Wystaw zamówienie"
      confirmLoading={issuing}
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={(values) => void issueOrder(values)}>
        <Form.Item name="productId" label="Produkt" rules={[{ required: true, message: "Wybierz produkt" }]}>
          <Select
            showSearch
            optionFilterProp="label"
            options={productOptions}
            onChange={handleProductChange}
            placeholder="Wybierz produkt"
          />
        </Form.Item>
        <Form.Item name="priceId" label="Cena" rules={[{ required: true, message: "Wybierz cenę" }]}>
          <Select
            options={[
              ...prices.map((price) => ({
                value: price.id,
                label: `${price.customLabel ? `${price.customLabel} – ` : ""}${getHumanPrice(
                  price.unit_amount ?? 0,
                  price.currency,
                )}`,
              })),
              { value: CUSTOM_PRICE_OPTION, label: t("order.issueManualCustomPriceOption") },
            ]}
            onChange={handlePriceChange}
            placeholder="Wybierz cenę"
          />
        </Form.Item>
        <Form.Item name="customLabel" label="Etykieta ceny">
          <Input />
        </Form.Item>
        <Form.Item name="amount" label="Kwota" rules={[{ required: true, message: "Podaj kwotę" }]}>
          <InputNumber
            min={0}
            step={0.01}
            precision={2}
            addonAfter={(selectedPrice?.currency ?? getProductCurrency(selectedProduct)).toUpperCase()}
            style={{ width: "100%" }}
          />
        </Form.Item>
        {customPriceSelected ? (
          <ManualOrderCustomPriceFields
            priceType={customPriceType}
            interval={customPriceInterval}
            skipImpactOnClientAccess={skipImpactOnClientAccess}
          />
        ) : null}
        <Form.Item name="sendClientNotification" label="Wyślij powiadomienie do klienta" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};
